import { useEffect, useState } from "react";
import {
  buildSyntheticReceipt,
  buildIllustrativeReceipt,
} from "../lib/receipt";

const Field = ({ label, value, mono = true, breakAll = true }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </p>
    <p
      className={`mt-1 text-sm text-foreground ${mono ? "font-mono" : ""} ${
        breakAll ? "break-all" : ""
      }`}
    >
      {value === null || value === undefined || value === "" ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        String(value)
      )}
    </p>
  </div>
);

const HashBlock = ({ label, value }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </p>
    <pre className="mt-1 max-w-full overflow-x-auto whitespace-pre-wrap break-all border border-border bg-[#FAFAFA] p-3 font-mono text-[11px] leading-relaxed text-foreground">
      {value}
    </pre>
  </div>
);

const PanelHeader = ({ tag, tagTone, title, subtitle }) => {
  const toneClass =
    tagTone === "synthetic"
      ? "border-emerald-700/40 bg-white text-emerald-800"
      : "border-foreground/30 bg-foreground text-white";
  return (
    <div className="border-b border-border bg-[#FAFAFA] p-6">
      <span
        className={`inline-flex items-center gap-2 border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${toneClass}`}
      >
        <span
          className={`h-1.5 w-1.5 ${
            tagTone === "synthetic" ? "bg-emerald-600" : "bg-white"
          }`}
        />
        {tag}
      </span>
      <h4 className="mt-4 font-serif text-2xl font-light leading-tight tracking-tight text-foreground">
        {title}
      </h4>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        {subtitle}
      </p>
    </div>
  );
};

export const ReceiptPreview = ({ savedDraft, output, wallet, liveReceiptPresent = false }) => {
  const [synthetic, setSynthetic] = useState(null);
  const [illustrative, setIllustrative] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const s = buildSyntheticReceipt({ savedDraft, output, wallet });
        const i = await buildIllustrativeReceipt({ savedDraft, output, wallet });
        if (!cancelled) {
          setSynthetic(s);
          setIllustrative(i);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("ReceiptPreview build failed", e);
        if (!cancelled) setError(e.message || "Could not compose receipts.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [savedDraft, output, wallet]);

  if (error) {
    return (
      <div
        data-testid="receipt-preview-error"
        className="border border-destructive/40 bg-white p-6 font-mono text-[11px] uppercase tracking-[0.16em] text-destructive"
      >
        Could not compose receipts in this browser. {error}
      </div>
    );
  }

  if (!synthetic || !illustrative) {
    return (
      <p
        data-testid="receipt-preview-loading"
        className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground"
      >
        Composing receipts…
      </p>
    );
  }

  return (
    <div data-testid="receipt-preview" className="grid gap-px bg-border md:grid-cols-2">
      {/* SYNTHETIC */}
      <article
        data-testid="receipt-synthetic"
        className="flex flex-col bg-white"
      >
        <PanelHeader
          tag="Synthetic"
          tagTone="synthetic"
          title="What Cierge Labs persists today"
          subtitle="Exactly the fields stored against this draft. Not cryptographically signed. The studio makes no production-trust claim here."
        />
        <div className="grid gap-5 p-6">
          <Field label="Receipt id" value={synthetic.id} />
          <Field
            label="Issued at"
            value={new Date(synthetic.issued_at).toLocaleString()}
          />
          <Field
            label="Wallet fingerprint (bound)"
            value={synthetic.wallet_fingerprint || "— not bound (no wallet)"}
          />
          <Field
            label="Selection summary"
            value={synthetic.selection_summary}
            mono={false}
            breakAll={false}
          />
          <Field
            label="Cryptographically signed"
            value={synthetic.cryptographically_signed ? "yes" : "no"}
          />
          <p className="border-l-2 border-emerald-700/50 pl-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-muted-foreground">
            {synthetic.note}
          </p>
        </div>
      </article>

      {/* ILLUSTRATIVE */}
      <article
        data-testid="receipt-illustrative"
        className={`flex flex-col bg-white ${liveReceiptPresent ? "opacity-70" : ""}`}
      >
        {liveReceiptPresent && (
          <div
            data-testid="receipt-illustrative-reference-banner"
            className="border-b border-amber-700/40 bg-amber-50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-800"
          >
            Reference shape only · a live KATLAS signed receipt is present above
          </div>
        )}
        <PanelHeader
          tag={liveReceiptPresent ? "Reference · KATLAS shape" : "Illustrative · KATLAS shape"}
          tagTone="illustrative"
          title={liveReceiptPresent ? "Reference shape (not the receipt state)" : "What KATLAS would issue"}
          subtitle={
            liveReceiptPresent
              ? "A live KATLAS signed receipt is now available above. This panel is reference material only — it is NOT the receipt state for this draft."
              : wallet
                ? "Visitor's wallet signs the canonical payload. Issuer DID, authority, and expiry remain illustrative — KATLAS would issue these in production."
                : "No wallet present. Signature field shown empty. Issuer DID and authority remain illustrative."
          }
        />
        <div className="grid gap-5 p-6">
          <Field label="Issuer (illustrative DID)" value={illustrative.payload.issuer} />
          <Field
            label="Issued at / Expires at"
            value={`${new Date(
              illustrative.payload.issued_at,
            ).toLocaleString()} → ${new Date(
              illustrative.payload.expires_at,
            ).toLocaleString()}`}
            mono={false}
            breakAll={false}
          />
          <Field
            label="Purpose"
            value={illustrative.payload.purpose}
          />
          <Field
            label="Authority · role"
            value={illustrative.payload.authority.role_label}
            mono={false}
            breakAll={false}
          />
          <Field
            label="Authority · mandate"
            value={illustrative.payload.authority.mandate}
          />
          <Field
            label="Subject · wallet fingerprint"
            value={
              illustrative.payload.subject.wallet_fingerprint ||
              "— not bound (no wallet)"
            }
          />
          <Field
            label="Governance scope"
            value={illustrative.payload.governance_scope.label}
            mono={false}
            breakAll={false}
          />
          <HashBlock
            label="Content hash · sha256(canonical_payload)"
            value={illustrative.content_hash}
          />
          <HashBlock
            label={`Signature · ${illustrative.signature.alg} ${
              illustrative.signature.signed ? "(real, by visitor wallet)" : "(empty)"
            }`}
            value={
              illustrative.signature.value ||
              "— no signature: no wallet present"
            }
          />
          <p className="border-l-2 border-foreground pl-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-muted-foreground">
            {illustrative.note}
          </p>
        </div>
      </article>
    </div>
  );
};
